import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Collapse,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
    useTheme,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import { useMenuContext } from '../../context/MenuContext';
import { useAssistant } from '../../context/AssistantContext';
import { toPersianDigits } from '../../utils/convertNumbers';
import { branchListSx, childSx } from './navStyles';

export const ASSISTANT_LABEL = 'دستیار هوشمند';

const rowSx = (theme, active) => ({
    borderRadius: '.8rem',
    minHeight: '3rem',
    py: '0.5rem',
    pr: '1rem',
    gap: 1,
    bgcolor: active ? theme.palette.button.active : 'transparent',
    '& .MuiListItemText-primary': {
        fontWeight: active ? 'bold' : 'normal',
        color: active ? theme.palette.text.active : theme.palette.text.default,
    },
    '& .MuiSvgIcon-root': { color: active ? theme.palette.text.active : theme.palette.text.default },
    '&:hover': {
        bgcolor: theme.palette.button.active,
        '& .MuiListItemText-primary, & .MuiSvgIcon-root': { color: theme.palette.text.active },
    },
    '& .MuiListItemIcon-root': { minWidth: 0 },
});

// «نمای کلی» link — the dashboard home at /dashboard.
export function OverviewNavItem() {
    const theme = useTheme();
    const { selectedMenuItem } = useMenuContext();
    const active = !selectedMenuItem;
    return (
        <ListItemButton
            component={Link}
            to="/dashboard"
            replace={active}
            aria-current={active ? 'page' : undefined}
            sx={rowSx(theme, active)}
        >
            <ListItemIcon>
                <SpaceDashboardOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="نمای کلی" sx={{ m: 0, textAlign: 'right' }} />
        </ListItemButton>
    );
}

// «دستیار هوشمند» branch: new / recent (≤5) / all conversations. Open while
// the assistant is on screen; collapsed elsewhere to keep the menu short.
export default function AssistantNav() {
    const theme = useTheme();
    const { selectedMenuItem } = useMenuContext();
    const assistant = useAssistant();
    const onAssistant = selectedMenuItem === 'assistant';
    const [open, setOpen] = useState(onAssistant);
    const [menu, setMenu] = useState({ anchor: null, conversation: null });

    useEffect(() => setOpen(onAssistant), [onAssistant]);

    if (!assistant?.enabled) return null;
    const { recent, recentLoading, recentError, selectedId, view, openNew, openHistory } = assistant;

    const closeMenu = () => setMenu({ anchor: null, conversation: null });

    return (
        <Box component="li" sx={{ listStyle: 'none' }}>
            {/* The chevron sits over the row's end so both read as one button. */}
            <Box sx={{ position: 'relative', '&:hover > .MuiListItemButton-root': { bgcolor: theme.palette.button.active } }}>
                <ListItemButton
                    component={Link}
                    to="/dashboard/assistant"
                    aria-current={onAssistant && view === 'chat' && !selectedId ? 'page' : undefined}
                    sx={{ ...rowSx(theme, onAssistant), paddingInlineEnd: theme.spacing(5) }}
                >
                    <ListItemIcon>
                        <AutoAwesomeOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={ASSISTANT_LABEL} sx={{ m: 0, textAlign: 'right' }} />
                </ListItemButton>
                <IconButton
                    size="small"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    aria-controls="assistant-nav-branch"
                    aria-label={open ? 'بستن گفتگوهای دستیار' : 'نمایش گفتگوهای دستیار'}
                    sx={{
                        position: 'absolute',
                        insetInlineEnd: 11,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: onAssistant ? theme.palette.text.active : theme.palette.text.default,
                    }}
                >
                    {open ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </Box>

            <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                    id="assistant-nav-branch"
                    dense
                    disablePadding
                    aria-label="گفتگوهای دستیار"
                    sx={branchListSx(theme)}
                >
                    <ListItemButton sx={childSx(theme, false)} onClick={openNew}>
                        <ListItemIcon>
                            <AddIcon />
                        </ListItemIcon>
                        <ListItemText primary="گفتگوی جدید" />
                    </ListItemButton>

                    {recentLoading && recent.length === 0 && (
                        <Typography variant="caption" sx={{ display: 'block', px: 1, py: 0.5, opacity: 0.7 }}>
                            در حال بارگذاری…
                        </Typography>
                    )}
                    {recentError && recent.length === 0 && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', px: 1, py: 0.5 }}>
                            {recentError}
                        </Typography>
                    )}

                    {recent.map((conversation) => {
                        const active = onAssistant && selectedId === conversation.id;
                        const title = conversation.title || 'بدون عنوان';
                        return (
                            <Box
                                key={conversation.id}
                                component="li"
                                sx={{ display: 'flex', alignItems: 'center', listStyle: 'none' }}
                            >
                                <ListItemButton
                                    component={Link}
                                    to={`/dashboard/assistant?conversation=${conversation.id}`}
                                    aria-current={active ? 'page' : undefined}
                                    title={title}
                                    sx={{ ...childSx(theme, active), flex: 1, minWidth: 0 }}
                                >
                                    <ListItemText
                                        primary={toPersianDigits(title)}
                                        primaryTypographyProps={{ noWrap: true }}
                                    />
                                </ListItemButton>
                                <IconButton
                                    size="small"
                                    aria-label={`گزینه‌های گفتگوی «${title}»`}
                                    aria-haspopup="menu"
                                    onClick={(event) =>
                                        setMenu({ anchor: event.currentTarget, conversation })
                                    }
                                    sx={{ color: theme.palette.text.default, p: 0.5 }}
                                >
                                    <MoreVertIcon sx={{ fontSize: '1.05rem' }} />
                                </IconButton>
                            </Box>
                        );
                    })}

                    <ListItemButton
                        sx={childSx(theme, onAssistant && view === 'history')}
                        onClick={openHistory}
                        aria-current={onAssistant && view === 'history' ? 'page' : undefined}
                    >
                        <ListItemIcon>
                            <HistoryIcon />
                        </ListItemIcon>
                        <ListItemText primary="همهٔ گفتگوها" />
                    </ListItemButton>
                </List>
            </Collapse>

            <ConversationMenu
                anchorEl={menu.anchor}
                conversation={menu.conversation}
                onClose={closeMenu}
                onRename={assistant.requestRename}
                onDelete={assistant.requestDelete}
            />
        </Box>
    );
}

export function ConversationMenu({ anchorEl, conversation, onClose, onRename, onDelete }) {
    return (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
            <MenuItem
                onClick={() => {
                    onClose();
                    onRename(conversation);
                }}
            >
                <ListItemIcon>
                    <EditOutlinedIcon fontSize="small" />
                </ListItemIcon>
                تغییر نام
            </MenuItem>
            <MenuItem
                onClick={() => {
                    onClose();
                    onDelete(conversation);
                }}
                sx={{ color: 'error.main' }}
            >
                <ListItemIcon sx={{ color: 'inherit' }}>
                    <DeleteOutlineIcon fontSize="small" />
                </ListItemIcon>
                حذف
            </MenuItem>
        </Menu>
    );
}
